import { UIKit } from "@/shared/ui";
/**
 * Виджет «Строка состояния»: сводка по чертежу и контекстная подсказка
 * по текущему инструменту — чтобы редактор был понятен без обучения. Справа — единица
 * длины для размеров и полей ввода (мм, см, м, дюймы): модель всё равно в миллиметрах.
 */
import { detectRooms } from "@/widgets/plan";
import { useLengthUnitStore, useProjectStore, ViewMode } from "@/shared/store";
import { Tool, useEditorStore, type WallMethod } from "@/widgets/editor";
import { LENGTH_UNIT_ORDER, LENGTH_UNITS, formatArea } from "@/shared/lib/geometry";

const UNIT_OPTIONS = LENGTH_UNIT_ORDER.map((value) => ({
	value,
	label: LENGTH_UNITS[value].label,
	title: `Размеры в единицах: ${LENGTH_UNITS[value].label}`,
}));

/** В 3D инструменты черчения не работают — подсказка своя. */
const HINT_3D =
	"Перетаскивание — поворот камеры, колесо — зум, правая кнопка — сдвиг. Клик выделяет предмет: тяните его — переедет, тяните кружок со стрелкой — повернётся, R — на 90°. Клик по двери открывает её, двойной клик по лампе или выключателю — свет.";

/** В развёртках свои действия: там ставят розетки, а не чертят стены. */
const HINT_ELEVATION =
	"Выберите тип точки слева и кликните по стене. Точку можно тянуть мышью, Alt — без привязок.";

const HINT_ESTIMATE =
	"Объёмы считаются по чертежу: поправьте план - смета пересчитается. Цены и количество можно вписать свои.";

/** У стены подсказка — по способу черчения. */
const WALL_METHOD_HINTS: Record<WallMethod, string> = {
	chain:
		"Клик - начало стены, следующий клик - продолжение цепочки. Ctrl+Z или Backspace - убрать последнюю стену. Правый клик - закончить, последняя стена выделится: длину можно поправить справа. Цифры - точная длина. Alt - без привязок.",
	single:
		"Клик - начало стены, второй клик - конец, следующая стена начнётся заново; построенная выделится - длину можно поправить справа. Можно нажать и протянуть. Цифры - точная длина. Alt - без привязок.",
	rectangle:
		"Клик в угол комнаты, клик в противоположный - или нажмите и протяните. Размеры в чистоте, можно ввести: 3000 2000 и Enter. У готовой стены комната прилипает к ней. Esc - отмена.",
	rotated:
		"Два клика - первая стена под любым углом (или введите длину), третий - ширина комнаты (или введите её). Esc - отмена.",
};

const HINTS: Record<Tool, string> = {
	[Tool.Select]:
		"Тяните угол или стену, чтобы изменить планировку. Тяните пустое место или с пробелом — сдвиг вида, два пальца по тачпаду — тоже; колесо или щипок — зум.",
	[Tool.Wall]:
		"Клик — начало стены, следующий клик — продолжение цепочки. Esc или правый клик — закончить. Alt — без привязок. Пробел + перетаскивание или два пальца — сдвинуть вид, колесо или щипок — зум, цепочка не прервётся.",
	[Tool.Door]: "Кликните по стене, чтобы вставить дверь.",
	[Tool.Window]: "Кликните по стене, чтобы вставить окно.",
	[Tool.Place]:
		"Выберите объект в каталоге слева и кликните, куда его поставить: на стену или на пол.",
	[Tool.Cable]:
		"Кликните по розетке, выключателю, распредкоробке, щитку или кондиционеру — начнётся кабель. Клики по плану ведут трассу под прямыми углами, клик по прибору подключает. Esc — отменить.",
	[Tool.LightingConnection]:
		"Кликните по светильнику и настенному электроприбору, чтобы добавить соединительную линию. Esc — отменить.",
	[Tool.Pipe]:
		"Кликните по стояку, коллектору или водорозетке — начнётся труба. Клики по плану ведут трассу под прямыми углами, клик по другой точке подключает. Esc — отменить.",
	[Tool.Erase]:
		"Наведите на стену, предмет, розетку, дверь, кабель или комментарий — он подсветится красным, клик удалит. Ctrl+Z — вернуть, клик по пустому месту — выйти из удаления.",
	[Tool.Room]:
		"Кликните, где поставить комнату: размеры — в чистоте, от стены до стены, стены — выбранного типа и толщины. Esc — отмена.",
	[Tool.UnderlayScale]:
		"Масштаб подложки: кликните в начало и в конец стены, длину которой знаете, — потом введите её длину. Esc — отмена.",
	[Tool.UnderlayMove]: "Тяните план, чтобы совместить его с чертежом. Esc — готово.",
	[Tool.Comment]:
		"Кликните по предмету, розетке, двери или стене — комментарий привяжется к нему, по пустому месту — будет свободным. Пишите текст, Enter — готово. Двойной клик — править, правый клик — шрифт, цвет, удалить.",
};

export const StatusBar = () => {
	const graph = useProjectStore((s) => s.project.layers.walls);
	const tool = useEditorStore((s) => s.tool);
	const wallMethod = useEditorStore((s) => s.wallMethod);
	const viewMode = useEditorStore((s) => s.viewMode);
	const unit = useLengthUnitStore((s) => s.unit);
	const setUnit = useLengthUnitStore((s) => s.setUnit);
	// Единица нужна там, где есть размеры: на плане и на развёртках
	const showUnits = viewMode !== ViewMode.ThreeD && viewMode !== ViewMode.Estimate;

	const rooms = detectRooms(graph);
	const total = rooms.reduce((sum, room) => sum + room.area, 0);

	return (
		<div className="hidden items-center justify-between gap-4 border-t border-border bg-surface px-4 text-xs text-ink md:flex">
			<UIKit.Text as="span" className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
				{viewMode === ViewMode.ThreeD
					? HINT_3D
					: viewMode === ViewMode.Elevation
						? HINT_ELEVATION
						: viewMode === ViewMode.Estimate
							? HINT_ESTIMATE
							: tool === Tool.Wall
								? WALL_METHOD_HINTS[wallMethod]
								: HINTS[tool]}
			</UIKit.Text>
			<UIKit.Text as="span" className="shrink-0 whitespace-nowrap tabular-nums">
				Комнат: {rooms.length} · Общая площадь: {formatArea(total)} · Стен:{" "}
				{Object.keys(graph.walls).length}
			</UIKit.Text>
			{showUnits && (
				<UIKit.SegmentedControl value={unit} options={UNIT_OPTIONS} onChange={setUnit} />
			)}
		</div>
	);
};
